"use client"

import { Button } from "@opensea/next-ui-kit"
import type { ButtonProps } from "@opensea/ui-kit"
import {
  isEvmAddress,
  useIsConnecting,
  useIsReady,
  useSiwe,
} from "@opensea/wallet"
import { useTranslations } from "next-intl"
import { useEffect, useRef } from "react"
import { useConnectFlow } from "@/hooks/use-connect-flow"
import { useAddress, useAuthenticated } from "@/hooks/wallet"

type ConnectButtonProps = ButtonProps & {
  attemptConnect?: boolean
  requireAuthentication?: boolean
  connectedChildren?: React.ReactNode
  onConnectedClick?: (address: string) => void
  skeletonFallback?: React.ReactNode
}

export function ConnectButton({
  attemptConnect = false,
  requireAuthentication = false,
  children,
  connectedChildren,
  onConnectedClick,
  skeletonFallback,
  ...props
}: ConnectButtonProps) {
  const connectFlow = useConnectFlow({ source: "ConnectButton" })
  const isConnecting = useIsConnecting()
  const ready = useIsReady()
  const address = useAddress()
  const authenticated = useAuthenticated()
  const t = useTranslations("ConnectButton")
  const { isSiwePending, promptSiwe } = useSiwe()

  const hasPromptedSiweRef = useRef(false)
  const lastAddressRef = useRef<string | undefined>(undefined)

  // Auto-connect flow
  useEffect(() => {
    if (attemptConnect && ready && !isConnecting && !address) {
      connectFlow()
    }
  }, [
    attemptConnect,
    ready,
    isConnecting,
    address,
    connectFlow,
    requireAuthentication,
  ])

  // Reset tracking when address changes (including account switches)
  useEffect(() => {
    if (address !== lastAddressRef.current) {
      hasPromptedSiweRef.current = false
      lastAddressRef.current = address
    }
  }, [address])

  // Show skeleton when not ready or connecting (if provided)
  if ((!ready || isConnecting) && skeletonFallback) {
    return <>{skeletonFallback}</>
  }

  // User has address but needs authentication
  if (address && requireAuthentication && !authenticated) {
    return (
      <Button
        {...props}
        disabled={isSiwePending || props.disabled}
        isLoading={isSiwePending || props.isLoading}
        onClick={() => promptSiwe()}
      >
        {children ||
          (isEvmAddress(address)
            ? t("signInWithEthereumButton")
            : t("signInWithSolanaButton"))}
      </Button>
    )
  }

  // User has address and is authenticated (or auth not required)
  if (address) {
    return (
      <Button
        {...props}
        disabled={props.disabled}
        isLoading={props.isLoading}
        onClick={
          onConnectedClick ? () => onConnectedClick(address) : props.onClick
        }
      >
        {connectedChildren || children || t("cta")}
      </Button>
    )
  }

  // User has no address - show connect button
  return (
    <Button
      {...props}
      disabled={isConnecting || props.disabled}
      isLoading={isConnecting || props.isLoading}
      onClick={event => {
        connectFlow({
          event,
        })
      }}
    >
      {children || t("cta")}
    </Button>
  )
}
